import React, { useState } from "react";
import {
  Box,
  Heading,
  useToast,
  Input,
  Textarea,
  Button,
  Select,
  FormControl,
  FormLabel,
  Stack,
} from "@chakra-ui/react";
import { submitReport } from "../../api/problemReports";
import Translate from "../LanguageTranslation/Translate";
import Header from "./Header";

const ReportProblem = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    customSubject: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const finalSubject =
        formData.subject === "Other"
          ? formData.customSubject
          : formData.subject;

      const payload = {
        ...formData,
        subject: finalSubject,
        email: localStorage.getItem("email"),
      };

      const response = await submitReport(payload);

      if (response.success) {
        toast({
          title: "Success",
          description: response.message || "Problem report submitted successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setFormData({
          name: "",
          subject: "",
          customSubject: "",
          description: "",
        });
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to submit problem report",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Unable to report the problem";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------- UI -------------------------------------------------- */
  return (
    <Box w="full" h="100vh">
      <Header title={<Translate text="Report a Problem" />} />

      <Box
        bg="white"
        boxShadow="lg"
        borderRadius="lg"
        p={{ base: 4, md: 8 }}
        mx="auto"
        mt={8}
        w={{ base: "full", md: "600px" }}
      >
        <Heading size="md" mb={6} textAlign="left">
          <Translate text="Please describe the issue you have encountered." />
        </Heading>

        <form onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {/* Name */}
            <FormControl isRequired isDisabled={loading}>
              <FormLabel>
                <Translate text="Your Name" />
              </FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                borderRadius="md"
              />
            </FormControl>

            {/* Subject */}
            <FormControl isRequired isDisabled={loading}>
              <FormLabel>
                <Translate text="Subject" />
              </FormLabel>
              <Select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Select Subject"
                borderRadius="md"
              >
                <option value="Login or Account Issue">
                  <Translate text="Login or Account Issue" />
                </option>
                <option value="Payment or Billing Problem">
                  <Translate text="Payment or Billing Problem" />
                </option>
                <option value="Bug or Technical Error">
                  <Translate text="Bug or Technical Error" />
                </option>
                <option value="Feature Not Working as Expected">
                  <Translate text="Feature Not Working as Expected" />
                </option>
                <option value="Other">
                  <Translate text="Other (please specify)" />
                </option>
              </Select>
            </FormControl>

            {/* Custom subject if Other */}
            {formData.subject === "Other" && (
              <FormControl isRequired isDisabled={loading}>
                <FormLabel>
                  <Translate text="Specify Subject" />
                </FormLabel>
                <Input
                  name="customSubject"
                  value={formData.customSubject}
                  onChange={handleChange}
                  placeholder="e.g. Missing translation on dashboard"
                  borderRadius="md"
                />
              </FormControl>
            )}

            {/* Description */}
            <FormControl isRequired isDisabled={loading}>
              <FormLabel>
                <Translate text="Description" />
              </FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Provide as much detail as possible…"
                resize="vertical"
                borderRadius="md"
                minH="120px"
              />
            </FormControl>

            <Button
              type="submit"
              bg="#286328"
              color="white"
              _hover={{ bg: "#245724" }}
              isLoading={loading}
              loadingText={<Translate text="Submitting…" />}
              borderRadius="md"
            >
              <Translate text="Submit" />
            </Button>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default ReportProblem;
